import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { supabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingScreen } from '../common/LoadingScreen';
import { Plus, Edit2, Save, X, ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from '../../lib/toast';

interface WikiPage {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export function WikiPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState<WikiPage | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [pages, setPages] = useState<WikiPage[]>([]);
  const { user } = useAuth();

  // Get user role
  const userRole = 
    user?.user_metadata?.role || 
    user?.role ||
    (user?.email === 'andmaillev@gmail.com' ? 'admin' : 'client');

  const canEdit = userRole === 'admin' || user?.email === 'andmaillev@gmail.com';
  const isNewPage = window.location.pathname === '/wiki/new';

  useEffect(() => {
    const loadPages = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabaseClient
          .from('wiki_pages')
          .select('*')
          .order('title');

        if (error) throw error;
        setPages(data || []);

        // If no specific page is selected and we have pages, load the first one
        if (!id && !isNewPage && data && data.length > 0) {
          navigate(`/wiki/${data[0].id}`);
          return;
        }

        // If creating a new page, set editing mode
        if (isNewPage) {
          setEditing(true);
          setEditContent('');
          setEditTitle('');
          setPage(null);
        }
        // If a specific page is selected, load it
        else if (id) {
          const { data: pageData, error: pageError } = await supabaseClient
            .from('wiki_pages')
            .select('*')
            .eq('id', id)
            .single();

          if (pageError) throw pageError;
          setPage(pageData);
          setEditContent(pageData.content);
          setEditTitle(pageData.title);
        }
      } catch (err) {
        console.error('Error loading wiki pages:', err);
        toast.error('Failed to load wiki pages');
      } finally {
        setLoading(false);
      }
    };

    loadPages();
  }, [id, navigate, isNewPage]);

  const handleSave = async () => {
    if (!editTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      if (id && !isNewPage) {
        // Update existing page
        const { error } = await supabaseClient
          .from('wiki_pages')
          .update({
            title: editTitle,
            content: editContent,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
        
        // Refresh page data
        const { data: refreshedData, error: refreshError } = await supabaseClient
          .from('wiki_pages')
          .select('*')
          .eq('id', id)
          .single();

        if (refreshError) throw refreshError;
        setPage(refreshedData);
      } else {
        // Create new page
        const { data, error } = await supabaseClient
          .from('wiki_pages')
          .insert({
            title: editTitle,
            content: editContent
          })
          .select()
          .single();

        if (error) throw error;
        
        // Navigate to the new page
        navigate(`/wiki/${data.id}`);
        setPage(data);
      }

      setEditing(false);
      toast.success('Page saved successfully');
      
      // Refresh pages list
      const { data: refreshedPages, error: pagesError } = await supabaseClient
        .from('wiki_pages')
        .select('*')
        .order('title');

      if (pagesError) throw pagesError;
      setPages(refreshedPages || []);

    } catch (err) {
      console.error('Error saving wiki page:', err);
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !page) return;

    const confirmed = window.confirm(`Are you sure you want to delete "${page.title}"? This action cannot be undone.`);
    if (!confirmed) return;

    setDeleting(true);
    try {
      const { error } = await supabaseClient
        .from('wiki_pages')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Page deleted successfully');
      
      // Refresh pages list
      const { data: refreshedPages, error: pagesError } = await supabaseClient
        .from('wiki_pages')
        .select('*')
        .order('title');

      if (pagesError) throw pagesError;
      setPages(refreshedPages || []);

      // Navigate to first available page or root
      if (refreshedPages && refreshedPages.length > 0) {
        navigate(`/wiki/${refreshedPages[0].id}`);
      } else {
        navigate('/wiki');
      }

    } catch (err) {
      console.error('Error deleting wiki page:', err);
      toast.error('Failed to delete page');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="bg-white rounded-lg shadow min-h-screen">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/wiki')}
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              {editing ? 'Editing: ' : ''}{isNewPage ? 'New Page' : page?.title || 'Wiki Pages'}
            </h2>
          </div>
          <div className="flex gap-2">
            {canEdit && !editing && !isNewPage && (
              <>
                <button
                  onClick={() => navigate('/wiki/new')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Page
                </button>
                <button
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex">
          {/* Sidebar with page list */}
          <div className="w-64 border-r border-gray-200 pr-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pages</h3>
            <nav className="space-y-1">
              {pages.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/wiki/${p.id}`)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                    p.id === id
                      ? 'bg-indigo-50 text-indigo-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {p.title}
                </button>
              ))}
              {canEdit && (
                <button
                  onClick={() => navigate('/wiki/new')}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                    isNewPage
                      ? 'bg-indigo-50 text-indigo-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  + New Page
                </button>
              )}
            </nav>
          </div>

          {/* Main content area */}
          <div className="flex-1 pl-6">
            {editing || isNewPage ? (
              <div className="space-y-4">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Page Title"
                  className="w-full px-4 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
                <MDEditor
                  value={editContent}
                  onChange={(val) => setEditContent(val || '')}
                  height={500}
                />
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setEditing(false);
                      if (page) {
                        setEditContent(page.content);
                        setEditTitle(page.title);
                      } else {
                        navigate('/wiki');
                      }
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="prose max-w-none">
                {page ? (
                  <MDEditor.Markdown source={page.content} />
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p>Select a page from the sidebar or create a new one.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}